import {Component, computed, effect, inject, input} from '@angular/core';
import {DEFAULT_OAUTH_FLOW_STATE, OauthFlowService} from '../oauth-flow.service';
import {toSignal} from '@angular/core/rxjs-interop';
import {timer} from 'rxjs';
import {Router} from '@angular/router';
import {WINDOW_TOKEN} from '../../core/tokens/window.token';

@Component({
  selector: 'app-oauth-flow',
  imports: [],
  templateUrl: './oauth-flow.component.html',
  styleUrl: './oauth-flow.component.scss'
})
export class OauthFlowComponent {
  showActions = input(false);
  redirectOnSuccess = input<string | undefined>(undefined);
  redirectOnError = input<string | undefined>(undefined);
  autoInitiate = input(true);

  private readonly flowService = inject(OauthFlowService);
  private readonly window = inject(WINDOW_TOKEN);
  private readonly router = inject(Router);

  // Track if callback processing has been initiated to prevent multiple attempts
  private callbackProcessingInitiated = false;

  // Cache the initial callback state before URL gets cleared
  private readonly initialCallbackState = this.window.location.search.includes('code=') || this.window.location.search.includes('error=');

  readonly state = toSignal(this.flowService.state$, {initialValue: DEFAULT_OAUTH_FLOW_STATE});

  // Computed signals for UI state
  readonly isProcessing = computed(() => this.state().isProcessing);
  readonly isAuthenticated = computed(() => this.state().isAuthenticated);
  readonly hasError = computed(() => !!this.state().error);
  readonly stage = computed(() => this.state().stage);

  // Processing message computed signal
  readonly processingMessage = computed(() => {
    switch (this.state().stage) {
      case 'initiating':
        return 'Redirecting to Login...';
      case 'callback':
        return 'Processing Authentication...';
      default:
        return 'Please Wait...';
    }
  });

  // Processing hint computed signal
  readonly processingHint = computed(() => {
    switch (this.state().stage) {
      case 'initiating':
        return 'You will be redirected to the login page.';
      case 'callback':
        return 'Verifying your credentials...';
      default:
        return '';
    }
  });

  // Error display computed signals
  readonly errorName = computed(() =>
    this.state().error?.name || this.state().error?.code || 'Authentication Error'
  );

  readonly errorMessage = computed(() =>
    this.state().error?.message ||
    'An unexpected error occurred during authentication.'
  );


  constructor() {
    // Effect for initial flow handling
    effect(() => {
      const auto = this.autoInitiate();
      const isAuth = this.isAuthenticated();
      const processing = this.isProcessing();

      if (this.initialCallbackState && !this.callbackProcessingInitiated) {
        // Handle OAuth callback once using cached initial state
        this.callbackProcessingInitiated = true;
        this.flowService.processCallback();
      } else if (!this.initialCallbackState && auto && !isAuth && !processing) {
        // Auto-initiate authentication
        this.flowService.initiateAuthentication();
      }
    });

    // Effect for success redirect
    effect((onCleanup) => {
      const redirectUrl = this.redirectOnSuccess();
      const stage = this.stage();

      let sub: { unsubscribe: () => void } | undefined;
      if (stage === 'authenticated' && redirectUrl) {
        // Schedule navigation after delay without using setTimeout
        sub = timer(1000).subscribe(() => this.router.navigate([redirectUrl]));
      }
      onCleanup(() => sub?.unsubscribe());
    });

    // Effect for error redirect
    effect((onCleanup) => {
      const redirectUrl = this.redirectOnError();
      const stage = this.stage();

      let sub: { unsubscribe: () => void } | undefined;
      if (stage === 'error' && redirectUrl) {
        // Schedule navigation after delay without using setTimeout
        sub = timer(2000).subscribe(() => this.router.navigate([redirectUrl]));
      }
      onCleanup(() => sub?.unsubscribe());
    });
  }

  // Action methods for template
  retry(): void {
    this.flowService.initiateAuthentication();
  }

  logout(): void {
    this.flowService.logout();
    this.router.navigate(['/']);
  }

  cancel(): void {
    try {
      if (this.window.history.length > 1) {
        this.window.history.back();
        return;
      }
    } catch (err) { console.error(err); }
    const fallback = this.isAuthenticated() ? '/overview' : '/';
    this.router.navigate([fallback]);
  }

  refreshToken(): void {
    this.flowService.refreshToken();
  }

}
